import Typography from "@portal/components/atoms/v2/typography";
import { RiskChart } from "./RiskChart";
import useFundPriceHistory from "@portal/hooks/useFundPriceHistory/[isin]";
import { useMemo } from "react";
import PageSpinner from "@portal/components/molecules/v2/pageSpinner";
import useUsesNorneFunds from "@portal/hooks/useUsesNorneFunds";
import { RobotTextResources } from "@portal/assets/text/RobotTextResources";

interface Props {
  centerId: string;
  isDefaultLanguage: boolean;
}

export const RiskInfo = ({ centerId, isDefaultLanguage }: Props) => {
  const { data: isUsingNorneFunds } = useUsesNorneFunds(centerId);
  const texts = isDefaultLanguage ? RobotTextResources.primary : RobotTextResources.secondary;

  const { fundPriceHistoryList: norneAksje, isLoading: norneAksjeIsLoading } = useFundPriceHistory({
    isin: "NO0010867682",
    fromDate: "2020-12-09",
  });
  const { fundPriceHistoryList: norne80, isLoading: norne80IsLoading } = useFundPriceHistory({
    isin: "NO0010922867",
    fromDate: "2020-12-09",
  });
  const { fundPriceHistoryList: norne50, isLoading: norne50IsLoading } = useFundPriceHistory({
    isin: "SE0015194634",
    fromDate: "2020-12-09",
  });
  const { fundPriceHistoryList: norne20, isLoading: norne20IsLoading } = useFundPriceHistory({
    isin: "SE0015194626",
    fromDate: "2020-12-09",
  });

  const { fundPriceHistoryList: boreaAksje, isLoading: boreaAksjeIsLoading } = useFundPriceHistory({
    isin: "NO0010867682",
    fromDate: "2026-01-01",
  });
  const { fundPriceHistoryList: borea80, isLoading: borea80IsLoading } = useFundPriceHistory({
    isin: "NO0013712893",
    fromDate: "2026-01-01",
  });
  const { fundPriceHistoryList: borea50, isLoading: borea50IsLoading } = useFundPriceHistory({
    isin: "NO0013712885",
    fromDate: "2026-01-01",
  });
  const { fundPriceHistoryList: borea20, isLoading: borea20IsLoading } = useFundPriceHistory({
    isin: "NO0013712877",
    fromDate: "2026-01-01",
  });

  const sortedNorneAksjeData = useMemo(() => {
    norneAksje?.sort((a, b) => {
      return Date.parse(a.navDate) - Date.parse(b.navDate);
    });
    return norneAksje;
  }, [norneAksje]);

  const sortedNorne80Data = useMemo(() => {
    norne80?.sort((a, b) => {
      return Date.parse(a.navDate) - Date.parse(b.navDate);
    });
    return norne80;
  }, [norne80]);

  const sortedNorne50Data = useMemo(() => {
    norne50?.sort((a, b) => {
      return Date.parse(a.navDate) - Date.parse(b.navDate);
    });
    return norne50;
  }, [norne50]);

  const sortedNorne20Data = useMemo(() => {
    norne20?.sort((a, b) => {
      return Date.parse(a.navDate) - Date.parse(b.navDate);
    });
    return norne20;
  }, [norne20]);

  const sortedBoreaAksjeData = useMemo(() => {
    boreaAksje?.sort((a, b) => {
      return Date.parse(a.navDate) - Date.parse(b.navDate);
    });
    return boreaAksje;
  }, [boreaAksje]);

  const sortedBorea80Data = useMemo(() => {
    borea80?.sort((a, b) => {
      return Date.parse(a.navDate) - Date.parse(b.navDate);
    });
    return borea80;
  }, [borea80]);

  const sortedBorea50Data = useMemo(() => {
    borea50?.sort((a, b) => {
      return Date.parse(a.navDate) - Date.parse(b.navDate);
    });
    return borea50;
  }, [borea50]);

  const sortedBorea20Data = useMemo(() => {
    borea20?.sort((a, b) => {
      return Date.parse(a.navDate) - Date.parse(b.navDate);
    });
    return borea20;
  }, [borea20]);

  const series = useMemo(() => {
    if (isUsingNorneFunds) {
      return [
        { name: "Norne Aksje", color: "#4a90e2", priceHistory: sortedNorneAksjeData ?? [] },
        { name: "Norne Kombi 80", color: "#f76c6c", priceHistory: sortedNorne80Data ?? [] },
        { name: "Norne Kombi 50", color: "#ffbd01", priceHistory: sortedNorne50Data ?? [] },
        { name: "Norne Kombi 20", color: "#50c878", priceHistory: sortedNorne20Data ?? [] },
      ];
    } else {
      return [
        { name: "Borea Aksje", color: "#4a90e2", priceHistory: sortedBoreaAksjeData ?? [] },
        { name: "Borea Kombi 80", color: "#f76c6c", priceHistory: sortedBorea80Data ?? [] },
        { name: "Borea Kombi 50", color: "#ffbd01", priceHistory: sortedBorea50Data ?? [] },
        { name: "Borea Kombi 20", color: "#50c878", priceHistory: sortedBorea20Data ?? [] },
      ];
    }
  }, [
    isUsingNorneFunds,
    sortedNorneAksjeData,
    sortedNorne80Data,
    sortedNorne50Data,
    sortedNorne20Data,
    sortedBoreaAksjeData,
    sortedBorea80Data,
    sortedBorea50Data,
    sortedBorea20Data,
  ]);

  const isLoading =
    norneAksjeIsLoading ||
    norne50IsLoading ||
    norne80IsLoading ||
    norne20IsLoading ||
    boreaAksjeIsLoading ||
    borea80IsLoading ||
    borea50IsLoading ||
    borea20IsLoading;

  return (
    <div className="flex flex-col my-3 px-1">
      <Typography variant="Body">{isUsingNorneFunds ? texts.PAGE_5_INFO_TEXT_A : texts.PAGE_5_INFO_TEXT_BOREA_A}</Typography>
      {isLoading ? (
        <PageSpinner />
      ) : (
        <div className="flex flex-col my-3 border dark:border-blueBorderDark rounded-md w-full p-1 bg-pageBoxBackgroundLight dark:bg-pageBoxBackgroundDark">
          <RiskChart series={series} />
        </div>
      )}
      <Typography variant="Body">{texts.PAGE_5_INFO_TEXT_B}</Typography>
      <Typography variant="Body">{texts.PAGE_5_INFO_TEXT_C}</Typography>
    </div>
  );
};
